import { useState, useRef, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuthStore } from 'shared/store/authStore'
import { ChevronDown, LayoutDashboard, Building2, Shield, LogOut } from 'lucide-react'

export type UserMenuVariant = 'light' | 'dark'
export type UserMenuLayout = 'dropdown' | 'inline'

export interface UserMenuProps {
  /** When provided, Login button opens this (e.g. modal). Otherwise links to /. */
  onOpenLogin?: () => void
  /** When provided, Register button opens this. Otherwise links to /register. */
  onOpenRegister?: () => void
  /** When provided, "Register company" opens this modal instead of linking to /company/register. */
  onOpenRegisterCompany?: () => void
  variant?: UserMenuVariant
  /** Inline: stacked full-width links (for mobile drawer). Dropdown: toggle + floating menu. */
  layout?: UserMenuLayout
}

export default function UserMenu({ onOpenLogin, onOpenRegister, onOpenRegisterCompany, variant = 'light', layout = 'dropdown' }: UserMenuProps) {
  const { t } = useTranslation('portal')
  const navigate = useNavigate()
  const customer = useAuthStore((s) => s.customer)
  const me = useAuthStore((s) => s.me)
  const logoutCustomer = useAuthStore((s) => s.logoutCustomer)
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const isInline = layout === 'inline'

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    if (open) document.addEventListener('click', handleClickOutside)
    return () => document.removeEventListener('click', handleClickOutside)
  }, [open])

  const isDark = variant === 'dark'
  const buttonBase = isDark
    ? 'text-slate-300 hover:text-white hover:bg-slate-800'
    : 'text-slate-700 hover:text-blue-600 hover:bg-slate-50'
  const dropdownBg = isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'
  const dropdownItem = isDark
    ? 'text-slate-300 hover:bg-slate-700 hover:text-white'
    : 'text-slate-700 hover:bg-slate-50'

  const handleLogout = () => {
    setOpen(false)
    logoutCustomer()
    navigate('/')
  }

  const loginBtnClass = `text-sm font-medium rounded-lg transition-colors ${buttonBase}`
  const registerBtnClass = `text-sm font-semibold rounded-lg transition-colors shadow-sm ${
    isDark ? 'bg-emerald-600 text-white hover:bg-emerald-500' : 'bg-blue-600 text-white hover:bg-blue-700'
  }`

  if (!customer) {
    return (
      <div className={`flex items-center ${isInline ? 'w-full flex-col gap-2' : 'gap-2'}`}>
        {onOpenLogin ? (
          <button
            type="button"
            onClick={onOpenLogin}
            data-testid="nav-login"
            className={`${isInline ? 'w-full px-4 py-3' : 'px-3 py-2'} ${loginBtnClass}`}
          >
            {t('nav.login')}
          </button>
        ) : (
          <Link
            to="/"
            data-testid="nav-login"
            className={`${isInline ? 'w-full px-4 py-3 text-center block' : 'px-3 py-2'} ${loginBtnClass}`}
          >
            {t('nav.login')}
          </Link>
        )}
        {onOpenRegister ? (
          <button
            type="button"
            onClick={onOpenRegister}
            data-testid="nav-register"
            className={`${isInline ? 'w-full px-4 py-3' : 'px-4 py-2'} ${registerBtnClass}`}
          >
            {t('nav.register')}
          </button>
        ) : (
          <Link
            to="/register"
            data-testid="nav-register"
            className={`${isInline ? 'w-full px-4 py-3 text-center block' : 'px-4 py-2'} ${registerBtnClass}`}
          >
            {t('nav.register')}
          </Link>
        )}
      </div>
    )
  }

  const displayName = me?.company_name ?? customer.user?.email ?? ''
  const hasCompany = Boolean(me?.has_company)
  const hasAdmin = Boolean(me?.is_admin)

  if (isInline) {
    const linkClass = `flex items-center gap-2 px-4 py-3 text-sm rounded-lg transition-colors w-full text-left ${dropdownItem}`
    return (
      <div className="w-full">
        <div className={`px-3 py-2 text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-slate-500' : 'text-slate-500'}`}>
          {t('nav.userMenu')}
        </div>
        <ul className="flex flex-col gap-0.5 mt-1" role="menu">
          <li role="none">
            <Link to="/dashboard" role="menuitem" className={linkClass} data-testid="user-menu-customer-dashboard">
              <LayoutDashboard className="w-4 h-4 shrink-0" />
              {t('nav.customerDashboard')}
            </Link>
          </li>
          {hasCompany ? (
            <li role="none">
              <Link to="/company" role="menuitem" className={linkClass} data-testid="user-menu-company-dashboard">
                <Building2 className="w-4 h-4 shrink-0" />
                {t('nav.companyDashboard')}
              </Link>
            </li>
          ) : (
            <li role="none">
              {onOpenRegisterCompany ? (
                <button
                  type="button"
                  onClick={() => { onOpenRegisterCompany(); setOpen(false) }}
                  role="menuitem"
                  className={linkClass}
                  data-testid="user-menu-register-company"
                >
                  <Building2 className="w-4 h-4 shrink-0" />
                  {t('nav.registerCompany')}
                </button>
              ) : (
                <Link to="/company/register" role="menuitem" className={linkClass} data-testid="user-menu-register-company">
                  <Building2 className="w-4 h-4 shrink-0" />
                  {t('nav.registerCompany')}
                </Link>
              )}
            </li>
          )}
          {hasAdmin && (
            <li role="none">
              <Link to="/admin" role="menuitem" className={linkClass} data-testid="user-menu-admin-panel">
                <Shield className="w-4 h-4 shrink-0" />
                {t('nav.adminPanel')}
              </Link>
            </li>
          )}
          <li role="none" className={`border-t mt-1 pt-1 ${isDark ? 'border-slate-700' : 'border-slate-200'}`}>
            <button
              type="button"
              onClick={handleLogout}
              role="menuitem"
              className={`flex w-full items-center gap-2 px-4 py-3 text-sm rounded-lg transition-colors ${dropdownItem}`}
              data-testid="user-menu-logout"
            >
              <LogOut className="w-4 h-4 shrink-0" />
              {t('nav.logout')}
            </button>
          </li>
        </ul>
      </div>
    )
  }

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors border ${buttonBase} ${
          isDark ? 'border-slate-700 bg-slate-800/50' : 'border-slate-200 bg-white'
        }`}
        aria-expanded={open}
        aria-haspopup="menu"
        aria-label={t('nav.userMenu')}
        data-testid="user-menu-toggle"
      >
        <span className="max-w-[8rem] truncate">{displayName}</span>
        <ChevronDown className={`w-4 h-4 shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <ul
          className={`absolute top-full right-0 mt-1 min-w-[12rem] max-w-[min(20rem,calc(100vw-2rem))] py-1 rounded-lg border shadow-lg z-50 ${dropdownBg}`}
          role="menu"
        >
          <li role="none">
            <Link
              to="/dashboard"
              onClick={() => setOpen(false)}
              role="menuitem"
              className={`flex items-center gap-2 px-3 py-2 text-sm transition-colors ${dropdownItem}`}
              data-testid="user-menu-customer-dashboard"
            >
              <LayoutDashboard className="w-4 h-4 shrink-0" />
              {t('nav.customerDashboard')}
            </Link>
          </li>
          {hasCompany ? (
            <li role="none">
              <Link
                to="/company"
                onClick={() => setOpen(false)}
                role="menuitem"
                className={`flex items-center gap-2 px-3 py-2 text-sm transition-colors ${dropdownItem}`}
                data-testid="user-menu-company-dashboard"
              >
                <Building2 className="w-4 h-4 shrink-0" />
                {t('nav.companyDashboard')}
              </Link>
            </li>
          ) : (
            <li role="none">
              {onOpenRegisterCompany ? (
                <button
                  type="button"
                  onClick={() => { onOpenRegisterCompany(); setOpen(false) }}
                  role="menuitem"
                  className={`flex items-center gap-2 px-3 py-2 text-sm transition-colors w-full text-left ${dropdownItem}`}
                  data-testid="user-menu-register-company"
                >
                  <Building2 className="w-4 h-4 shrink-0" />
                  {t('nav.registerCompany')}
                </button>
              ) : (
                <Link
                  to="/company/register"
                  onClick={() => setOpen(false)}
                  role="menuitem"
                  className={`flex items-center gap-2 px-3 py-2 text-sm transition-colors ${dropdownItem}`}
                  data-testid="user-menu-register-company"
                >
                  <Building2 className="w-4 h-4 shrink-0" />
                  {t('nav.registerCompany')}
                </Link>
              )}
            </li>
          )}
          {hasAdmin && (
            <li role="none">
              <Link
                to="/admin"
                onClick={() => setOpen(false)}
                role="menuitem"
                className={`flex items-center gap-2 px-3 py-2 text-sm transition-colors ${dropdownItem}`}
                data-testid="user-menu-admin-panel"
              >
                <Shield className="w-4 h-4 shrink-0" />
                {t('nav.adminPanel')}
              </Link>
            </li>
          )}
          <li role="none" className={`border-t mt-1 pt-1 ${isDark ? 'border-slate-700' : 'border-slate-200'}`}>
            <button
              type="button"
              onClick={handleLogout}
              role="menuitem"
              className={`flex w-full items-center gap-2 px-3 py-2 text-sm transition-colors ${dropdownItem}`}
              data-testid="user-menu-logout"
            >
              <LogOut className="w-4 h-4 shrink-0" />
              {t('nav.logout')}
            </button>
          </li>
        </ul>
      )}
    </div>
  )
}
